import { Box, Skeleton } from "@mui/material";
import React, { useMemo } from "react"; // Import useMemo

// Constants matching your demand screen layout
const JOB_SIDEBAR_WIDTH = 210;
const PATTERN_SIDEBAR_WIDTH = 210;
const DAYS = 7;
const BORDER_COLOR = "#E5E5E5";

// Simulates a few rows for the skeleton display
const DUMMY_WORKCENTERS = [
    { jobs: [{ patterns: 3 }, { patterns: 2 }] },
    { jobs: [{ patterns: 4 }] },
];

/**
 * Skeleton for the top header row with dates.
 */
function DateHeaderSkeleton() {
    return (
        <Box display='flex' width='100%' sx={{ minWidth: "1200px" }}>
            <Skeleton variant='rectangular' width={JOB_SIDEBAR_WIDTH} height={56} />
            <Skeleton variant='rectangular' width={PATTERN_SIDEBAR_WIDTH} height={56} sx={{ ml: "1px" }}/>
            <Box display='flex' flex={1}>
                {Array.from({ length: DAYS }).map((_, i) => (
                    <Box key={i} flex={1} sx={{ borderLeft: `1px solid ${BORDER_COLOR}` }}>
                        <Box p={1} height={56} borderTop={`1px solid ${BORDER_COLOR}`} borderBottom={`1px solid ${BORDER_COLOR}`}>
                            <Skeleton variant='text' width='50%' />
                            <Skeleton variant='text' width='30%' />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Skeleton for the collapsible workcenter header row.
 */
function WorkcenterHeaderSkeleton() {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
                p: 2,
            }}
        >
            <Skeleton variant='circular' width={24} height={24} />
            <Skeleton variant='text' width={150} height={32} />
        </Box>
    );
}

/**
 * Skeleton for a single shift pattern row (card + daily inputs).
 */
function PatternRowSkeleton() {
    const dayVisibility = useMemo(
        () => Array.from({ length: DAYS }, () => Math.random() > 0.4),
        [] // Empty dependency array means this runs only on the first render
    );

    return (
        <Box display='flex' alignItems='center' sx={{ height: "92px", boxSizing: "border-box", paddingBottom: "12px" }}>
            {/* Shift Pattern Card Skeleton */}
            <Box sx={{ width: PATTERN_SIDEBAR_WIDTH, display: "flex", p: "8px 12px" }}>
                <Box sx={{ width: "100%", border: `1px solid ${BORDER_COLOR}`, borderRadius: "9px", p: "12px 16px" }}>
                    <Skeleton variant='text' width="80%" height={24} />
                    <Skeleton variant='text' width="60%" height={20} />
                </Box>
            </Box>

            {/* Daily Input Skeletons */}
            <Box display='flex' flex={1}>
                {Array.from({ length: DAYS }).map((_, i) => (
                    <Box key={i} flex={1} display='flex' alignItems='center' justifyContent='center' p={1}>
                        {/* Use the stable, pre-calculated random pattern */}
                        {dayVisibility[i] && (
                            <Skeleton variant='rectangular' width="90px" height="60px" sx={{ borderRadius: "8px" }} />
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Skeleton for an entire Job Section (Job Name + multiple Pattern Rows).
 */
function JobSectionSkeleton({ job }: { job: { patterns: number } }) {
    return (
        <Box display='flex' sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
            {/* Job Name Cell Skeleton */}
            <Box sx={{ width: JOB_SIDEBAR_WIDTH, p: "12px 16px" }}>
                <Skeleton variant='text' width='70%' height={28} />
            </Box>

            {/* Container for all pattern rows */}
            <Box display='flex' flexDirection='column' flex={1}>
                {Array.from({ length: job.patterns }).map((_, i) => (
                    <PatternRowSkeleton key={i} />
                ))}
            </Box>
        </Box>
    );
}

/**
 * The main exported skeleton component for the entire Demand Screen.
 */
export default function ShiftDemandSkeleton({
    isLoading,
}: {
    isLoading: boolean;
}) {
    if (!isLoading) {
        return null;
    }

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box sx={{ minWidth: "1200px" }}>
                <DateHeaderSkeleton />
                {DUMMY_WORKCENTERS.map((workcenter, i) => (
                    <Box key={i}>
                        <WorkcenterHeaderSkeleton />
                        {workcenter.jobs.map((job, j) => (
                            <JobSectionSkeleton key={j} job={job} />
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
